// src/pages/updates/news.tsx
import React, { useMemo, useState, useEffect } from "react";
import {
    Box,
    Button,
    Modal,
    TextField,
    Typography,
    Alert,
    Divider,
    Stack,
} from "@mui/material";
import {
    Add,
    PictureAsPdf,
    TableChart,
    Search,
    RestartAlt,
} from "@mui/icons-material";
import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { StatusChip } from "../../../components/common/StatusChip";
import { RowActions } from "../../../components/common/RowActions";
import { ExportLoadingBackdrop } from "../../../components/common/ExportLoadingBackdrop";
import { useTableState } from "../../../hooks/useTableState";
import { Notify } from "../../../layouts/notify";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
    useGetNewsQuery,
    useChangeNewsStatusMutation,
} from "../../../services/updates.query";

const NewsPage: React.FC = () => {
    const navigate = useNavigate();

    const { filters, setFilters, resetFilters } = useTableState({
        search: "",
        page: 1,
    });

    const [searchInput, setSearchInput] = useState(filters.search || "");
    const page = filters.page || 1;
    const pageSize = 100;

    useEffect(() => {
        setSearchInput(filters.search || "");
    }, [filters.search]);

    const [imgOpen, setImgOpen] = useState(false);
    const [imgSrc, setImgSrc] = useState<string>("");

    const [isExporting, setIsExporting] = useState(false);
    const [exportProgressText, setExportProgressText] = useState("");

    const {
        data: newsData,
        isLoading,
        isFetching,
        isError,
    } = useGetNewsQuery("");

    const [changeStatus, { isLoading: toggling }] = useChangeNewsStatusMutation();

    const openImage = (src: string) => {
        setImgSrc(src);
        setImgOpen(true);
    };

    const hasActiveFilters = Boolean(filters.search);

    const handleApplySearch = () => {
        setFilters({ search: searchInput.trim(), page: 1 });
    };

    const handleResetFilters = () => {
        setSearchInput("");
        resetFilters();
    };

    const filteredData = useMemo(() => {
        const q = (filters.search || "").toLowerCase().trim();
        if (!q) return newsData || [];
        return (newsData || []).filter((row: any) =>
            `${row?.title || ""}`.toLowerCase().includes(q)
        );
    }, [filters.search, newsData]);

    const paginatedData = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, page, pageSize]);

    const handleExportPdf = async () => {
        try {
            setIsExporting(true);
            setExportProgressText("Preparing news PDF...");
            const doc = new jsPDF("landscape");
            doc.setFontSize(16);
            doc.text("MTRCB - News Articles Report", 14, 15);
            doc.setFontSize(9);
            doc.setTextColor(100);
            doc.text(
                `Generated on: ${format(new Date(), "MMMM dd, yyyy hh:mm a")} | Total Records: ${filteredData.length}`,
                14,
                22
            );

            const tableRows = filteredData.map((item: any, index: number) => [
                index + 1,
                item.date ? format(new Date(item.date), "yyyy-MM-dd") : "—",
                item.title || "—",
                item.is_active ? "DISPLAY" : "HIDDEN",
                item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "—",
            ]);

            autoTable(doc, {
                head: [["#", "Date", "Title", "Status", "Date Created"]],
                body: tableRows,
                startY: 27,
                theme: "grid",
                styles: { fontSize: 8, cellPadding: 2 },
                headStyles: { fillColor: [0, 114, 188] },
            });

            doc.save(`mtrcb-news-${format(new Date(), "yyyyMMdd-HHmm")}.pdf`);
        } catch (err) {
            console.error(err);
            Notify("error", "Failed to export PDF.");
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            setIsExporting(true);
            setExportProgressText("Preparing news Excel...");
            const rows = filteredData.map((item: any, index: number) => ({
                "No.": index + 1,
                Date: item.date ? format(new Date(item.date), "yyyy-MM-dd") : "",
                Title: item.title || "",
                Status: item.is_active ? "DISPLAY" : "HIDDEN",
                "Date Created": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "",
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "News");
            XLSX.writeFile(
                workbook,
                `mtrcb-news-${format(new Date(), "yyyyMMdd-HHmm")}.xlsx`
            );
        } catch (err) {
            console.error(err);
            Notify("error", "Failed to export Excel.");
        } finally {
            setIsExporting(false);
        }
    };

    const columns = [
        {
            id: "rowNumber",
            header: "No.",
            size: 70,
            enableSorting: false,
            Cell: ({ row }: any) => {
                const globalIndex = (page - 1) * pageSize + row.index + 1;
                return (
                    <Typography fontSize={14}>
                        {globalIndex}
                    </Typography>
                );
            },
        },
        {
            accessorKey: "date",
            header: "Date",
            size: 150,
            Cell: ({ cell }: any) => {
                const v = cell.getValue();
                return <Typography fontSize={14}>{v ? format(new Date(v), "MMMM dd, yyyy") : "-"}</Typography>;
            },
        },
        {
            accessorKey: "attachments",
            header: "Image",
            size: 100,
            enableSorting: false,
            Cell: ({ cell }: any) => {
                const attachments = cell.getValue();
                const firstImage = attachments?.find((a: any) => a.type === "IMAGE")?.file_url;

                return firstImage ? (
                    <img
                        src={firstImage}
                        alt="Attachment"
                        style={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                        onClick={() => openImage(firstImage)}
                    />
                ) : (
                    <Typography fontSize={13} color="textSecondary">No Image</Typography>
                );
            },
        },
        {
            accessorKey: "title",
            header: "Title",
            size: 300,
            Cell: ({ cell }: any) => (
                <Typography fontSize={14} fontWeight={600} color="#111827">
                    {cell.getValue() || "—"}
                </Typography>
            ),
        },
        {
            accessorKey: "is_active",
            header: "Status",
            size: 130,
            Cell: ({ cell }: any) => {
                const active = !!cell.getValue();
                return (
                    <StatusChip
                        label={active ? "DISPLAY" : "HIDDEN"}
                        tone={active ? "success" : "neutral"}
                    />
                );
            },
        },
        {
            id: "actions",
            header: "Actions",
            size: 180,
            enableSorting: false,
            Cell: ({ row }: any) => {
                const item = row.original;
                return (
                    <RowActions
                        actions={[
                            {
                                label: "Edit",
                                onClick: () => navigate(`edit/${item.id}`),
                            },
                            {
                                label: item.is_active ? "Hide" : "Display",
                                tone: item.is_active ? "error" : "default",
                                loading: toggling,
                                onClick: () =>
                                    changeStatus({
                                        id: item.id,
                                        is_active: !item.is_active,
                                    }),
                            },
                        ]}
                        inline={2}
                    />
                );
            },
        },
    ];

    return (
        <DefaultPage
            title="News"
            subTitle="Manage news articles and updates."
            primaryButtonProps={{
                startIcon: <Add />,
                children: "Add New News",
                onClick: () => navigate("add"),
            }}
        >
            <ExportLoadingBackdrop
                open={isExporting}
                message={exportProgressText}
            />

            {/* Standardized Filter Container */}
            <Box
                sx={{
                    p: 2.5,
                    mb: 3,
                    backgroundColor: "#FFFFFF",
                    borderRadius: "6px",
                    border: "1px solid #E2E8F0",
                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
                }}
            >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                    <TextField
                        size="small"
                        label="Search News"
                        placeholder="Search by title..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleApplySearch();
                            }
                        }}
                        sx={{
                            minWidth: { xs: "100%", sm: 320 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "4px",
                                fontSize: "13.5px",
                            },
                        }}
                    />
                </Stack>

                <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<Search />}
                            onClick={handleApplySearch}
                            disabled={isFetching}
                            sx={{
                                borderRadius: "4px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "13px",
                                px: 2,
                                backgroundColor: "#0072BC",
                                "&:hover": { backgroundColor: "#005a96" },
                            }}
                        >
                            Search
                        </Button>
                        {hasActiveFilters && (
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<RestartAlt />}
                                onClick={handleResetFilters}
                                disabled={isFetching}
                                sx={{
                                    borderRadius: "4px",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "13px",
                                    px: 2,
                                    color: "#64748B",
                                    borderColor: "#CBD5E1",
                                    "&:hover": {
                                        borderColor: "#94A3B8",
                                        backgroundColor: "#F8FAFC",
                                    },
                                }}
                            >
                                Clear
                            </Button>
                        )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<PictureAsPdf />}
                            onClick={handleExportPdf}
                            disabled={isExporting || filteredData.length === 0}
                            sx={{
                                borderRadius: "4px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "13px",
                                px: 2,
                                color: "#334155",
                                borderColor: "#CBD5E1",
                                "&:hover": {
                                    borderColor: "#94A3B8",
                                    backgroundColor: "#F8FAFC",
                                },
                            }}
                        >
                            Export PDF
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<TableChart />}
                            onClick={handleExportExcel}
                            disabled={isExporting || filteredData.length === 0}
                            sx={{
                                borderRadius: "4px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "13px",
                                px: 2,
                                backgroundColor: "#0072BC",
                                "&:hover": { backgroundColor: "#005a96" },
                            }}
                        >
                            Export Excel
                        </Button>
                    </Box>
                </Box>
            </Box>

            {isError && (
                <Box mb={2}>
                    <Alert severity="error">
                        Failed to load news.
                    </Alert>
                </Box>
            )}

            <Box mb={2}>
                <Alert severity="success" sx={{ borderRadius: "6px" }}>
                    Total matched records: <strong>{Number(filteredData.length || 0).toLocaleString()}</strong>
                </Alert>
            </Box>

            <DataTableFive
                data={paginatedData}
                total={filteredData.length}
                page={page}
                pageSize={pageSize}
                onPageChange={(newPage) => setFilters({ page: newPage })}
                columns={columns as any}
                getRowId={(row: { id: any }) => String(row.id)}
                isLoading={isLoading}
                isFetching={isFetching}
                onRowClick={(row: any) => navigate(`edit/${row.original.id}`)}
            />

            {/* Full image modal */}
            <Modal open={imgOpen} onClose={() => setImgOpen(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 2,
                        borderRadius: "6px",
                    }}
                >
                    <img
                        src={imgSrc}
                        alt="Attachment Full View"
                        style={{ maxWidth: "90vw", maxHeight: "80vh", borderRadius: "4px" }}
                    />
                    <Typography variant="body2" sx={{ mt: 1, textAlign: "center" }}>
                        Click outside to close
                    </Typography>
                </Box>
            </Modal>
        </DefaultPage>
    );
};

export default NewsPage;
